import type { Category } from '../api/business/types';

/** The deepest a category tree goes — the API refuses deeper (R0030 WP-06). */
export const MAX_CATEGORY_DEPTH = 6;

/** A category of the tree in reading order, with the names from the top down to it. */
export type FlatCategory = { category: Category; path: Array<string> };

/** The tree in reading order — a parent, then its children — each with its path. */
export const flattenCategories = (tree: Array<Category>, path: Array<string> = []): Array<FlatCategory> =>
  tree.flatMap((category) => [
    { category, path: [...path, category.name] },
    ...flattenCategories(category.children, [...path, category.name]),
  ]);

export const findCategory = (tree: Array<Category>, id: string): Category | null => {
  for (const category of tree) {
    if (category.id === id) return category;

    const found = findCategory(category.children, id);

    if (found !== null) return found;
  }

  return null;
};

/** The category and every one under it. */
export const subtreeIds = (category: Category): Set<string> =>
  new Set([category.id, ...category.children.flatMap((child) => [...subtreeIds(child)])]);

/** How many levels the category and its subtree span: 1 without children. */
export const subtreeHeight = (category: Category): number =>
  1 + Math.max(0, ...category.children.map(subtreeHeight));

/**
 * Where the category may move: not under itself or anything under it, and not so deep that its subtree would
 * pass the limit. A new category (null) may go under any category above the last level.
 */
export const moveTargets = (tree: Array<Category>, category: Category | null): Array<FlatCategory> => {
  const excluded = category === null ? new Set<string>() : subtreeIds(category);
  const height = category === null ? 1 : subtreeHeight(category);

  return flattenCategories(tree).filter(
    (one) => !excluded.has(one.category.id) && one.category.depth + height <= MAX_CATEGORY_DEPTH,
  );
};

/** Its siblings in order, itself included: the children of its parent, or the top level. */
export const siblingsOf = (tree: Array<Category>, category: Category): Array<Category> =>
  category.parentId === null ? tree : (findCategory(tree, category.parentId)?.children ?? []);

/**
 * One level in: the category becomes the last child of the sibling above it. Null when it is first among its
 * siblings, or its subtree would pass the depth limit.
 */
export const indentTarget = (tree: Array<Category>, category: Category): string | null => {
  const siblings = siblingsOf(tree, category);
  const index = siblings.findIndex((one) => one.id === category.id);

  return index > 0 && category.depth + subtreeHeight(category) <= MAX_CATEGORY_DEPTH
    ? siblings[index - 1].id
    : null;
};

/** One level out: the category goes right after its parent, among its parent's siblings. Null at the top. */
export const outdentTarget = (
  tree: Array<Category>,
  category: Category,
): { parentId: string | null; position: number } | null => {
  if (category.parentId === null) return null;

  const parent = findCategory(tree, category.parentId);

  return parent === null ? null : { parentId: parent.parentId, position: parent.position + 1 };
};

/** The categories whose name, in any content language, has `needle` — with the categories above them. */
export const filterTree = (tree: Array<Category>, needle: string): Array<Category> => {
  if (needle === '') return tree;

  return tree.flatMap((category) => {
    const children = filterTree(category.children, needle);
    const matches = Object.values(category.translations).some((texts) =>
      (texts.name ?? '').toLocaleLowerCase('sk').includes(needle),
    );

    return matches || children.length > 0
      ? [{ ...category, children: matches ? category.children : children }]
      : [];
  });
};
